import { Pane, PaneType } from "../Types";
import { assert } from "../Utils";
import { getPaneIdFromPaneInput } from "./SandboxUtils";

async function formatCode(code: string) {
  const [prettier, parserBabel] = await Promise.all([
    import("prettier"),
    // @ts-ignore
    import("prettier/parser-babel"),
  ]);
  return prettier.format(code, {
    parser: "babel",
    plugins: [parserBabel],
  });
}

export async function formatCodeWithCursor(code: string, cursorOffset: number) {
  const [prettier, parserBabel] = await Promise.all([
    import("prettier"),
    // @ts-ignore
    import("prettier/parser-babel"),
  ]);
  return prettier.formatWithCursor(code, {
    cursorOffset,
    parser: "babel",
    plugins: [parserBabel],
  });
}

export async function convertPanesToCode(panes: Pane[]): Promise<string> {
  const processedPaneIds = new Set<string>();
  const outputBlocks: string[] = [];

  // @ts-ignore
  const globalPanes: (Pane & {
    type: PaneType.EvaluateGlobal;
  })[] = panes.filter((p) => p.type === PaneType.EvaluateGlobal);
  if (globalPanes.length === 1) {
    const p = globalPanes[0];
    outputBlocks.push(`const ${p.name} = await ${p.expression};`);
  } else if (globalPanes.length > 1) {
    outputBlocks.push(
      `const [${globalPanes
        .map((p) => p.name)
        .join(", ")}] = await Promise.all([${globalPanes.map(
        (p) => p.expression
      )}]);`
    );
  }
  globalPanes.forEach((p) => processedPaneIds.add(p.id));

  panes.forEach((p) => {
    if (p.type === PaneType.InputText) {
      outputBlocks.push(
        `${p.name !== undefined ? `// ${p.name}\n` : ""}const v_${
          p.id
        } = ${JSON.stringify(p.text)};`
      );
      processedPaneIds.add(p.id);
    }
  });

  // TODO: find connected components

  const mainBlock: string[] = [];
  const mainBlockPaneIds: string[] = [];
  function processPanes(panes: Pane[]) {
    panes.forEach((p) => {
      assert(p.type === PaneType.Evaluate);
      outputBlocks.push(
        `${p.name !== undefined ? `// ${p.name}\n` : ""}function f_${p.id}(${
          p.inputs.length > 0 ? "inputs" : ""
        }) {
  return ${p.expression};
}`
      );
      processedPaneIds.add(p.id);
      mainBlockPaneIds.push(p.id);
    });
    if (panes.length === 1) {
      const p = panes[0];
      assert(p.type === PaneType.Evaluate);
      mainBlock.push(
        `const v_${p.id} = await f_${p.id}(${
          p.inputs.length > 0
            ? `[${p.inputs
                .map((paneInput) => `v_${paneInput.source![0]}`)
                .join(",")}]`
            : ""
        });`
      );
    } else {
      mainBlock.push(
        `const [${panes
          .map((p) => `v_${p.id}`)
          .join(",")}] = await Promise.all([${panes
          .map((p) => {
            assert(p.type === PaneType.Evaluate);
            return `f_${p.id}(${
              p.inputs.length > 0
                ? `[${p.inputs
                    .map((paneInput) => `v_${paneInput.source![0]}`)
                    .join(",")}]`
                : ""
            })`;
          })
          .join(",")}]);`
      );
    }
  }

  while (true) {
    const panesReadyToGo = panes.filter(
      (p) =>
        p.type === PaneType.Evaluate &&
        !processedPaneIds.has(p.id) &&
        !p.inputs.some((paneInput) => {
          const paneId = getPaneIdFromPaneInput(paneInput);
          return paneId === undefined || !processedPaneIds.has(paneId);
        })
    );
    if (panesReadyToGo.length === 0) {
      break;
    }
    processPanes(panesReadyToGo);
  }
  if (mainBlockPaneIds.length > 0) {
    mainBlock.push(
      `return [${mainBlockPaneIds.map((id) => `v_${id}`).join(",")}];`
    );
  }

  return formatCode(`${outputBlocks.join("\n\n")}\n\nasync function main() {
${mainBlock.map((l) => `  ${l}\n`).join("")}}\n`);
}
